import { Helmet } from 'react-helmet-async';
import type { JobOffer } from '../../types/offres';
import { safeJsonLd } from '../../lib/utils/schema-utils';

// Mapping recruteurs.io → schema.org/JobPosting employmentType
// (Google for Jobs reconnaît : FULL_TIME, PART_TIME, CONTRACTOR,
// TEMPORARY, INTERN, VOLUNTEER, PER_DIEM, OTHER)
const EMPLOYMENT_TYPE_MAP: Record<string, string> = {
  cdi: 'FULL_TIME',
  cdd: 'TEMPORARY',
  freelance: 'CONTRACTOR',
  stage: 'INTERN',
  alternance: 'PART_TIME',
};

// Mapping département PACA → addressRegion (utile pour la précision
// régionale dans Google for Jobs)
function inferRegion(city: string | null | undefined): string {
  if (!city) return 'Provence-Alpes-Côte d\'Azur';
  const c = city.toLowerCase();
  if (/marseille|aix|toulon|cassis|aubagne|martigues|nice|cannes|antibes|grasse|fréjus|salon|arles|avignon|gap|digne/i.test(c)) {
    return 'Provence-Alpes-Côte d\'Azur';
  }
  if (/poitiers|niort|la rochelle|angoulême/i.test(c)) {
    return 'Nouvelle-Aquitaine';
  }
  return 'France';
}

interface JobPostingSchemaProps {
  offer: JobOffer;
  url: string;
}

export default function JobPostingSchema({ offer, url }: JobPostingSchemaProps) {
  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: offer.title,
    description: `${offer.description}\n\nProfil recherché :\n${offer.profile}`,
    identifier: {
      '@type': 'PropertyValue',
      name: offer.hiring_organization_name ?? offer.company_name ?? 'recruteurs.io',
      value: offer.internal_reference ?? offer.id,
    },
    datePosted: offer.published_at ?? offer.created_at,
    employmentType: EMPLOYMENT_TYPE_MAP[offer.contract_type] ?? 'OTHER',
    hiringOrganization: {
      '@type': 'Organization',
      name: offer.hiring_organization_name ?? offer.company_name ?? 'recruteurs.io',
      sameAs: offer.hiring_organization_url ?? 'https://recruteurs.io',
      ...(offer.hiring_organization_logo || offer.company_logo_url
        ? { logo: offer.hiring_organization_logo ?? offer.company_logo_url }
        : {}),
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: offer.location_city,
        addressRegion: inferRegion(offer.location_city),
        addressCountry: offer.job_location_country ?? 'FR',
        ...(offer.job_location_street_address && {
          streetAddress: offer.job_location_street_address,
        }),
        ...(offer.job_location_postal_code && {
          postalCode: offer.job_location_postal_code,
        }),
      },
    },
    url,
  };

  // validThrough : Google déprécie les offres après cette date.
  // Si absent, fallback à 60 jours après datePosted (recommandation
  // Google for Jobs).
  if (offer.valid_through) {
    jsonLd.validThrough = offer.valid_through;
  } else {
    const posted = new Date(offer.published_at ?? offer.created_at);
    const fallback = new Date(posted.getTime() + 60 * 24 * 60 * 60 * 1000);
    jsonLd.validThrough = fallback.toISOString();
  }

  if (offer.sector) jsonLd.industry = offer.sector;

  // Télétravail : jobLocationType + applicantLocationRequirements
  if (offer.remote_type !== 'no') {
    jsonLd.jobLocationType = 'TELECOMMUTE';
    jsonLd.applicantLocationRequirements = {
      '@type': 'Country',
      name: 'France',
    };
  }

  // Salaire : on accepte min seul, max seul, OU min + max (Google
  // affiche le snippet salaire même avec une seule valeur).
  if (offer.salary_min || offer.salary_max) {
    const valueObj: Record<string, unknown> = {
      '@type': 'QuantitativeValue',
      unitText: 'YEAR',
    };
    if (offer.salary_min && offer.salary_max) {
      valueObj.minValue = offer.salary_min;
      valueObj.maxValue = offer.salary_max;
    } else if (offer.salary_min) {
      valueObj.value = offer.salary_min;
    } else if (offer.salary_max) {
      valueObj.value = offer.salary_max;
    }
    jsonLd.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: offer.salary_currency ?? 'EUR',
      value: valueObj,
    };
  }

  // directApply = true → bouton "Postuler" reste sur Google for Jobs
  // (au lieu de rediriger). Active uniquement si on a un CTA d'apply
  // direct côté site (formulaire candidature embarqué = OK).
  jsonLd.directApply = true;

  return (
    <Helmet>
      <script type="application/ld+json">{safeJsonLd(jsonLd)}</script>
    </Helmet>
  );
}
